import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useStrategyStore } from '../../../../../../store/strategy-store/strategy-store';
import { formatDateLabel } from '../../../../strategy.helpers';
import { Metric } from '../../../../strategy.type';
import { ChartData, ChartDataItem, DataKey } from './initiative-progress-chart.type';

/**
 * Hook to generate chart data for initiative progress visualization
 *
 * @param metric - The metric type to display in the chart (e.g., Tasks, Story Points)
 * @returns An array of chart data items containing date, completed, and total values
 */
const useChartData = (metric: Metric): ChartData => {
  const initiative = useStrategyStore((state) => state.initiativePerformance);

  const {
    completed_subtasks_over_time: completedDataset = { [metric]: {} },
    total_subtasks_over_time: totalDataset = { [metric]: {} },
  } = initiative || {};

  const chartData = useMemo(() => {
    if (!initiative) {
      return [];
    }

    const dates = Object.keys(completedDataset[metric] || {}).sort();
    const currentDate = dayjs();

    return dates.map((date) => {
      const dateObj = dayjs(date, 'YYYY-MM');
      const isCurrentMonth = dateObj.isSame(currentDate, 'month');
      const isFuture = dateObj.isAfter(currentDate, 'month');
      const total = ((totalDataset[metric] || {}) as Record<string, number | null>)[date] || 0;
      const completed = ((completedDataset[metric] || {}) as Record<string, number | null>)[date] || 0;

      if (isFuture) {
        return {
          date: formatDateLabel(date),
          rawDate: date,
          [DataKey.TotalRegular]: null,
          [DataKey.CompletedRegular]: null,
          [DataKey.TotalFuture]: total ?? null,
          [DataKey.CompletedFuture]: completed ?? null,
          isCurrentMonth,
        } as ChartDataItem;
      } else {
        return {
          date: formatDateLabel(date),
          rawDate: date,
          [DataKey.TotalRegular]: total,
          [DataKey.CompletedRegular]: completed,
          [DataKey.TotalFuture]: isCurrentMonth ? total : null,
          [DataKey.CompletedFuture]: isCurrentMonth ? completed : null,
          isCurrentMonth,
        } as ChartDataItem;
      }
    });
  }, [completedDataset, totalDataset, metric, initiative]);

  return chartData;
};

export { useChartData };
